import { useState, useEffect } from 'react';
import {
  Clock,
  Users,
  Shield,
  Plus,
  Edit2,
  Trash2,
  AlertCircle,
  CheckCircle,
  Info,
  AlertTriangle,
  XCircle,
} from 'lucide-react';
import { businessRulesService, BusinessRule, RuleType } from '../../services/business-rules.service';
import { useAuthStore } from '../../stores/auth.store';
import CreateRuleModal from '../../components/settings/CreateRuleModal';
import EditRuleModal from '../../components/settings/EditRuleModal';

export default function BusinessRulesTabContent() {
  const user = useAuthStore((state) => state.user);
  const [rules, setRules] = useState<BusinessRule[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeSection, setActiveSection] = useState<'shift' | 'agent' | 'compliance'>('shift');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<BusinessRule | null>(null);

  useEffect(() => {
    loadRules();
  }, [user?.organizationId]);

  const loadRules = async () => {
    if (!user?.organizationId) return;

    setIsLoading(true);
    try {
      const allRules = await businessRulesService.getAll(user.organizationId);
      setRules(allRules);
    } catch (error) {
      console.error('Failed to load business rules:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteRule = async (id: string) => {
    if (!confirm('Are you sure you want to delete this rule?')) return;

    try {
      await businessRulesService.delete(id);
      await loadRules();
    } catch (error) {
      console.error('Failed to delete rule:', error);
      alert('Failed to delete rule. Please try again.');
    }
  };

  const handleToggleActive = async (rule: BusinessRule) => {
    try {
      await businessRulesService.update(rule.id, { isActive: !rule.isActive });
      await loadRules();
    } catch (error) {
      console.error('Failed to update rule:', error);
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'CRITICAL':
        return <XCircle className="h-4 w-4 text-red-600" />;
      case 'ERROR':
        return <AlertCircle className="h-4 w-4 text-orange-600" />;
      case 'WARNING':
        return <AlertTriangle className="h-4 w-4 text-yellow-600" />;
      case 'INFO':
        return <Info className="h-4 w-4 text-blue-600" />;
      default:
        return <CheckCircle className="h-4 w-4 text-gray-600" />;
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'CRITICAL':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'ERROR':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'WARNING':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'INFO':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const filterRulesBySection = (sectionType: string): BusinessRule[] => {
    const typeMap: Record<string, RuleType[]> = {
      shift: ['CHANNEL_HOURS', 'CAPACITY_TARGET'],
      agent: ['AGENT_CONSTRAINT', 'TEAM_CONSTRAINT'],
      compliance: ['LABOR_LAW', 'HOLIDAY'],
    };

    return rules.filter((rule) => typeMap[sectionType]?.includes(rule.ruleType));
  };

  const sections = [
    {
      id: 'shift',
      label: 'Shift Rules',
      icon: Clock,
      description: 'Define shift length, break requirements, and channel constraints',
      color: 'purple',
    },
    {
      id: 'agent',
      label: 'Agent Rules',
      icon: Users,
      description: 'Set weekly hours limits, consecutive days worked, and preferences',
      color: 'blue',
    },
    {
      id: 'compliance',
      label: 'Compliance & Labor Laws',
      icon: Shield,
      description: 'Enforce rest periods, weekend rotation, and legal requirements',
      color: 'green',
    },
  ];

  const sectionRules = filterRulesBySection(activeSection);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Business Rules</h2>
        <p className="text-gray-600 mt-1">
          Configure shift constraints, agent rules, and compliance requirements for your organization
        </p>
      </div>

      {/* Section Navigation */}
      <div className="grid grid-cols-3 gap-4">
        {sections.map((section) => {
          const Icon = section.icon;
          const isActive = activeSection === section.id;

          return (
            <button
              key={section.id}
              onClick={() => setActiveSection(section.id as any)}
              className={`p-4 rounded-lg border-2 transition-all text-left ${
                isActive
                  ? `border-${section.color}-500 bg-${section.color}-50`
                  : 'border-gray-200 hover:border-gray-300 bg-white'
              }`}
            >
              <div className="flex items-center space-x-3 mb-2">
                <Icon className={`h-6 w-6 ${isActive ? `text-${section.color}-600` : 'text-gray-600'}`} />
                <h3 className={`font-semibold ${isActive ? `text-${section.color}-900` : 'text-gray-900'}`}>
                  {section.label}
                </h3>
              </div>
              <p className="text-sm text-gray-600">{section.description}</p>
            </button>
          );
        })}
      </div>

      {/* Rules List */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {sections.find((s) => s.id === activeSection)?.label}
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              {sectionRules.length} rule{sectionRules.length !== 1 ? 's' : ''} configured
            </p>
          </div>
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            <Plus className="h-4 w-4" />
            <span>Add Rule</span>
          </button>
        </div>

        <div className="divide-y divide-gray-200">
          {sectionRules.length === 0 ? (
            <div className="p-12 text-center">
              <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No rules configured</h3>
              <p className="text-gray-600 mb-4">
                Get started by adding your first {sections.find((s) => s.id === activeSection)?.label.toLowerCase()}
              </p>
              <button
                onClick={() => setIsCreateModalOpen(true)}
                className="inline-flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                <Plus className="h-4 w-4" />
                <span>Add Rule</span>
              </button>
            </div>
          ) : (
            sectionRules.map((rule) => (
              <div key={rule.id} className="p-6 hover:bg-gray-50 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h4 className="text-lg font-semibold text-gray-900">{rule.name}</h4>
                      <span className={`flex items-center space-x-1 px-2 py-1 text-xs font-medium rounded border ${getSeverityColor(rule.severity)}`}>
                        {getSeverityIcon(rule.severity)}
                        <span>{rule.severity}</span>
                      </span>
                      {rule.isActive ? (
                        <span className="px-2 py-1 text-xs font-medium rounded bg-green-100 text-green-800">
                          Active
                        </span>
                      ) : (
                        <span className="px-2 py-1 text-xs font-medium rounded bg-gray-100 text-gray-800">
                          Inactive
                        </span>
                      )}
                    </div>
                    {rule.description && (
                      <p className="text-gray-600 text-sm mb-3">{rule.description}</p>
                    )}
                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      <span>Type: {rule.ruleType.replace(/_/g, ' ')}</span>
                      <span>•</span>
                      <span>Scope: {rule.scope}</span>
                      {rule.appliesTo && (
                        <>
                          <span>•</span>
                          <span>Applies to: {JSON.stringify(rule.appliesTo).length > 20 ? 'Custom' : rule.appliesTo}</span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 ml-4">
                    <button
                      onClick={() => handleToggleActive(rule)}
                      className={`p-2 rounded transition-colors ${
                        rule.isActive
                          ? 'text-green-600 hover:bg-green-50'
                          : 'text-gray-400 hover:bg-gray-100'
                      }`}
                      title={rule.isActive ? 'Deactivate rule' : 'Activate rule'}
                    >
                      <CheckCircle className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => setEditingRule(rule)}
                      className="p-2 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded transition-colors"
                      title="Edit rule"
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteRule(rule.id)}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                      title="Delete rule"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Modals */}
      {isCreateModalOpen && (
        <CreateRuleModal
          defaultSection={activeSection}
          onClose={() => setIsCreateModalOpen(false)}
          onSuccess={() => {
            setIsCreateModalOpen(false);
            loadRules();
          }}
        />
      )}

      {editingRule && (
        <EditRuleModal
          rule={editingRule}
          onClose={() => setEditingRule(null)}
          onSuccess={() => {
            setEditingRule(null);
            loadRules();
          }}
        />
      )}
    </div>
  );
}
